import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from 'src/constants/queryKeys';
import { useGetAuthData } from 'src/hooks';
import { useCallback, useState } from 'react';
import { getComplexUsers } from 'src/api/complexUsers';
import { IComplexUser } from 'src/typings/user';
import { Hydra, Response } from 'src/api/hydraResponse';
import { putAssignAllUsersToComplex, putComplexData } from 'src/api';
import { IComplex } from 'src/typings/complexes';

export const useComplexUsers = () => {
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();
  const [getParams, setGetParams] = useState<string>('');
  const {
    data: complexUsersData, isLoading: complexUsersDataIsLoading, refetch,
  } = useQuery({
    queryKey: [QUERY_KEYS.COMPLEX_USERS, getParams],
    queryFn: async (): Promise<Hydra<IComplexUser[]> | null> => {
      if (!jwt) return null;
      return (await getComplexUsers(jwt, getParams)).data;
    },
    enabled: !!jwt && !!getParams.length,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });
  const saveComplexUsers = useCallback((complexId: string, updatedComplex: { users: IComplexUser[] }) => {
    return new Promise<Response<IComplex>>((resolve, reject) => {
      putComplexData(jwt, complexId, updatedComplex)
        .then((response: Response<IComplex>) => resolve(response))
        .catch((error: Error) => reject(error));
    });
  }, [jwt]);

  const assignAllUsers = useCallback((complexId: string): Promise<Response<void>> => {
    return new Promise<Response<void>>((resolve, reject) => {
      putAssignAllUsersToComplex(jwt, complexId)
        .then((response: Response<void>) => resolve(response))
        .catch((error: Error) => reject(error));
    });
  }, []);

  return {
    complexUsersData,
    complexUsersDataIsLoading,
    refetch,
    setGetParams,
    saveComplexUsers,
    assignAllUsers,
  };
};
